import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { 
  Users, 
  FileSpreadsheet, 
  Activity, 
  TrendingUp, 
  Shield,
  Eye,
  UserCheck,
  UserX,
  Calendar,
  Building2,
  Settings,
  Edit,
  DollarSign,
  Crown,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Palette,
  Settings2,
  ChevronUp,
  ChevronDown,
  Briefcase
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
// Using alert instead of toast for compatibility
import { getErrorMessage } from "../../utils/errorUtils";
import { formatFeatureName } from '../../utils/featureNames';
import EnhancedFeatureSelector from './EnhancedFeatureSelector';
import CouponManager from './CouponManager';
import RoleBadge from './RoleBadge';
import { getFeaturesForTier, validateFeaturesForTier, TIER_HIERARCHY } from '../../utils/featureHierarchy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ENTERPRISE_API = `${BACKEND_URL}/api/enterprise`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSubsidiary, setSelectedSubsidiary] = useState("");
  const [roleDialogTab, setRoleDialogTab] = useState("assigned"); // NEW: "assigned" or "assign"
  const [selectedUserRoles, setSelectedUserRoles] = useState(null); // NEW: Store selected user's roles
  const [removingRoleId, setRemovingRoleId] = useState(null); // NEW: Track role being removed
  
  // Delete user state
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  
  // NEW: Search and Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [systemRoleFilter, setSystemRoleFilter] = useState("all"); // NEW: Platform Admin / Company User
  const [companyRoleFilter, setCompanyRoleFilter] = useState("all"); // NEW: Company Admin, Subsidiary Manager, etc.
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" or "hierarchy"
  const [userRoles, setUserRoles] = useState({}); // NEW: Store user roles by user_id
  
  // User pagination state
  const [userPage, setUserPage] = useState(1);
  const [usersPerPage] = useState(10);
  
  // Edit user state
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    email: "",
    full_name: "",
    company_name: ""
  });
  const [editUserLoading, setEditUserLoading] = useState(false);
  
  // Change password state
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  
  // Companies tab state
  const [companies, setCompanies] = useState([]); // NEW: Store companies from API
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companyPage, setCompanyPage] = useState(1); // NEW: Pagination
  const [companiesPerPage] = useState(10); // NEW: Items per page
  const [viewCompanyDetailsOpen, setViewCompanyDetailsOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // System Roles tab state
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRoleForm, setNewRoleForm] = useState({
    name: "",
    description: "",
    level: 5,
    permissions: []
  });

  useEffect(() => {
    // Temporarily disable admin check for testing
    fetchAdminData();
    // TODO: Re-enable after making beyemoges@gmail.com admin
    // if (user?.is_admin) {
    //   fetchAdminData();
    // }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      // Fetch data with proper error handling  
      const [statsResponse, usersResponse, rolesResponse, subsidiariesResponse, companiesResponse, revenueRes, subscriptionsRes, tiersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`).catch(() => ({data: {total_users: 0, active_users: 0, total_plans: 0}})),
        axios.get(`${API}/admin/users?include_role_info=true`).catch(() => ({data: []})), // NEW: Include role info
        axios.get(`${ENTERPRISE_API}/roles?exclude_platform=true`).catch(() => ({data: []})), // Exclude platform roles
        axios.get(`${ENTERPRISE_API}/subsidiaries?all=true`).catch(() => ({data: []})), // Get all subsidiaries for admin
        axios.get(`${API}/admin/companies/detailed`).catch(() => ({data: {companies: []}})), // NEW: Fetch companies
        axios.get(`${API}/admin/revenue-analytics`).catch(() => ({data: {mrr: 0, arr: 0, active_subscriptions: 0, revenue_by_tier: {}}})),
        axios.get(`${API}/admin/subscriptions`).catch(() => ({data: []})),
        axios.get(`${API}/subscription-tiers`).catch(() => ({data: []}))
      ]);

      setStats(statsResponse.data || {total_users: 0, active_users: 0, total_plans: 0});
      const usersData = usersResponse.data || [];
      setUsers(usersData);
      setRoles(rolesResponse.data || []);
      setSubsidiaries(subsidiariesResponse.data || []);
      setCompanies(companiesResponse.data?.companies || []); // NEW: Set companies from API
      setRevenueAnalytics(revenueRes.data || {mrr: 0, arr: 0, active_subscriptions: 0});
      setSubscriptions(subscriptionsRes.data || []);
      setSubscriptionTiers(tiersRes.data || []);
      
      // NEW: Fetch detailed roles for all users
      await fetchUserRoles(usersData);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      alert("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };
  
  // NEW: Fetch detailed role assignments for users
  const fetchUserRoles = async (usersData) => {
    try {
      const rolesMap = {};
      
      // Fetch roles for each user
      const rolePromises = usersData.map(async (user) => {
        try {
          const response = await axios.get(`${ENTERPRISE_API}/user-roles/by-user/${user.id}`);
          rolesMap[user.id] = response.data;
        } catch (error) {
          console.error(`Error fetching roles for user ${user.id}:`, error);
          rolesMap[user.id] = { company_roles: [], subsidiary_roles: [], platform_roles: [], all_assignments: [] };
        }
      });
      
      await Promise.all(rolePromises);
      setUserRoles(rolesMap);
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getUserStats = () => {
    if (!users.length) return [];
    
    // Group users by creation date
    const usersByDate = users.reduce((acc, user) => {
      const date = new Date(user.created_at).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(usersByDate)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .slice(-7) // Last 7 days
      .map(([date, count]) => ({ date, users: count }));
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) {
      toast.error("Please select both user and role");
      return;
    }

    try {
      // Get the selected role details to determine scope
      const role = roles.find(r => r.id === selectedRole);
      const roleScope = role?.role_scope || "subsidiary";
      
      // Validate subsidiary selection for subsidiary-scoped roles
      if (roleScope === "subsidiary" && !selectedSubsidiary) {
        toast.error("Please select a subsidiary for this role");
        return;
      }
      
      await axios.post(`${ENTERPRISE_API}/user-roles`, {
        user_id: selectedUser.id,
        role_id: selectedRole,
        scope_type: roleScope,
        scope_id: selectedSubsidiary || null,
        subsidiary_id: selectedSubsidiary || null  // Keep for backward compatibility
      });

      toast.success("Role assigned successfully!");
      
      // Refresh user's roles in dialog
      const response = await axios.get(`${ENTERPRISE_API}/user-roles/by-user/${selectedUser.id}`);
      setSelectedUserRoles(response.data);
      
      // Reset form and switch to assigned tab
      setSelectedRole("");
      setSelectedSubsidiary("");
      setRoleDialogTab("assigned");
      
      // Refresh main data
      fetchAdminData();
    } catch (error) {
      console.error('Assign role error:', error);
      toast.error(getErrorMessage(error, "Failed to assign role"));
    }
  };

  const handleToggleAdminStatus = async (userId, currentAdminStatus) => {
    const action = currentAdminStatus ? "remove admin privileges from" : "grant admin privileges to";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        is_admin: !currentAdminStatus
      });

      toast.success(`User ${currentAdminStatus ? "removed from" : "promoted to"} admin successfully!`);
      fetchAdminData(); // Refresh data
    } catch (error) {
      console.error('Toggle admin status error:', error);
      toast.error(getErrorMessage(error, "Failed to update admin status"));
    }
  };

  const handleToggleActiveStatus = async (userId, currentActiveStatus) => {
    const action = currentActiveStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        is_active: !currentActiveStatus
      });

      toast.success(`User ${action}d successfully!`);
      fetchAdminData(); // Refresh data
    } catch (error) {
      console.error('Toggle active status error:', error);
      toast.error(getErrorMessage(error, "Failed to update user status"));
    }
  };

  // Open edit user dialog
  const openEditUserDialog = (user) => {
    setEditingUser(user);
    setEditUserForm({
      email: user.email || "",
      full_name: user.full_name || "",
      company_name: user.company_name || ""
    });
    setEditUserDialogOpen(true);
  };

  // Handle edit user submit
  const handleEditUserSubmit = async () => {
    if (!editingUser) return;
    
    setEditUserLoading(true);
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, editUserForm);
      toast.success("User updated successfully!");
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchAdminData();
    } catch (error) {
      console.error('Edit user error:', error);
      toast.error(getErrorMessage(error, "Failed to update user"));
    } finally {
      setEditUserLoading(false);
    }
  };

  // Open change password dialog
  const openChangePasswordDialog = (user) => {
    setPasswordUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setChangePasswordDialogOpen(true);
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!passwordUser) return;
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setChangePasswordLoading(true);
    try {
      await axios.post(`${API}/admin/users/${passwordUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success("Password changed successfully!");
      setChangePasswordDialogOpen(false);
      setPasswordUser(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error('Change password error:', error);
      toast.error(getErrorMessage(error, "Failed to change password"));
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const openRoleDialog = async (user) => {
    setSelectedUser(user);
    setRoleDialogTab("assigned"); // Start with assigned roles tab
    setSelectedRole("");
    setSelectedSubsidiary("");
    
    // Fetch user's current roles
    try {
      const response = await axios.get(`${ENTERPRISE_API}/user-roles/by-user/${user.id}`);
      setSelectedUserRoles(response.data);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setSelectedUserRoles({ company_roles: [], subsidiary_roles: [], platform_roles: [], all_assignments: [] });
    }
    
    setRoleDialogOpen(true);
  };
  
  // NEW: Handle role removal
  const handleRemoveRole = async (assignmentId, roleName) => {
    if (!window.confirm(`Remove "${roleName}" role from this user?`)) {
      return;
    }
    
    setRemovingRoleId(assignmentId);
    
    try {
      await axios.delete(`${ENTERPRISE_API}/user-roles/${assignmentId}`);
      toast.success(`Role "${roleName}" removed successfully!`);
      
      // Refresh user's roles
      if (selectedUser) {
        const response = await axios.get(`${ENTERPRISE_API}/user-roles/by-user/${selectedUser.id}`);
        setSelectedUserRoles(response.data);
      }
      
      // Refresh main user list
      fetchAdminData();
    } catch (error) {
      console.error('Remove role error:', error);
      toast.error(getErrorMessage(error, "Failed to remove role"));
    } finally {
      setRemovingRoleId(null);
    }
  };

  const openViewUserDialog = (user) => {
    setViewingUser(user);
    setViewUserDialogOpen(true);
  };

  // NEW: Get unique companies for filter
  const getUniqueCompanies = () => {
    const companies = [...new Set(users.map(u => u.company_name).filter(Boolean))];
    return companies.sort();
  };

  // NEW: Filter users based on search and filters
  const getFilteredUsers = () => {
    return users.filter(user => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        user.full_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.company_name?.toLowerCase().includes(searchLower);
      
      // Company filter
      const matchesCompany = companyFilter === "all" || user.company_name === companyFilter;
      
      // OLD Role filter - for backward compatibility
      const matchesRole = roleFilter === "all" || 
        (roleFilter === "admin" && user.is_admin) ||
        (roleFilter === "user" && !user.is_admin);
      
      // NEW: System Role filter (Platform Admin vs Company User)
      const matchesSystemRole = systemRoleFilter === "all" ||
        (systemRoleFilter === "platform_admin" && user.is_admin) ||
        (systemRoleFilter === "company_user" && !user.is_admin);
      
      // NEW: Company Role filter
      const userRoleData = userRoles[user.id];
      let matchesCompanyRole = true;
      if (companyRoleFilter !== "all" && userRoleData) {
        const allRoles = userRoleData.all_assignments || [];
        matchesCompanyRole = allRoles.some(role => role.role_name === companyRoleFilter);
      }
      
      // Status filter
      const matchesStatus = statusFilter === "all" ||
        (statusFilter === "active" && user.is_active) ||
        (statusFilter === "inactive" && !user.is_active);
      
      return matchesSearch && matchesCompany && matchesRole && matchesSystemRole && matchesCompanyRole && matchesStatus;
    });
  };

  // NEW: Group users by company for hierarchy view
  const getGroupedUsers = () => {
    const filteredUsers = getFilteredUsers();
    const grouped = {};
    
    filteredUsers.forEach(user => {
      const companyName = user.company_name || 'No Company';
      if (!grouped[companyName]) {
        grouped[companyName] = [];
      }
      grouped[companyName].push(user);
    });
    
    return grouped;
  };

  // NEW: Filter companies by search (now uses API data)
  const getFilteredCompanies = () => {
    if (!companySearchTerm) return companies;
    
    const searchLower = companySearchTerm.toLowerCase();
    return companies.filter(company => 
      company.name?.toLowerCase().includes(searchLower) ||
      company.owner?.email?.toLowerCase().includes(searchLower) ||
      company.owner?.name?.toLowerCase().includes(searchLower)
    );
  };

  // NEW: Get paginated companies
  const getPaginatedCompanies = () => {
    const filtered = getFilteredCompanies();
    const startIndex = (companyPage - 1) * companiesPerPage;
    const endIndex = startIndex + companiesPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // NEW: Calculate total pages
  const getTotalCompanyPages = () => {
    const filtered = getFilteredCompanies();
    return Math.ceil(filtered.length / companiesPerPage);
  };

  // NEW: Open company details modal
  const openCompanyDetails = (company) => {
    setSelectedCompany(company);
    setViewCompanyDetailsOpen(true);
  };

  // NEW: Delete company (all users with that company name)
  const handleDeleteCompany = async (companyName) => {
    if (!window.confirm(`Delete all users from "${companyName}"? This cannot be undone!`)) {
      return;
    }

    try {
      // Get all users from this company
      const companyUsers = users.filter(u => u.company_name === companyName);
      
      // Delete each user using the correct admin endpoint
      let deletedCount = 0;
      for (const user of companyUsers) {
        try {
          await axios.delete(`${API}/admin/users/${user.id}`);
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete user ${user.email}:`, error);
        }
      }

      toast.success(`Deleted ${deletedCount} users from ${companyName}`);
      fetchAdminData(); // Refresh data
    } catch (error) {
      console.error('Delete company error:', error);
      toast.error(getErrorMessage(error, "Failed to delete company"));
    }
  };

  // Delete individual user with strong confirmation
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    
    // Verify confirmation text matches
    const expectedText = `DELETE ${deletingUser.email}`;
    if (deleteConfirmText !== expectedText) {
      toast.error(`Please type "${expectedText}" to confirm deletion`);
      return;
    }
    
    setDeleteUserLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${deletingUser.id}`);
      toast.success(`User "${deletingUser.email}" has been permanently deleted`);
      setDeleteUserDialogOpen(false);
      setDeletingUser(null);
      setDeleteConfirmText("");
      fetchAdminData(); // Refresh data
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(getErrorMessage(error, "Failed to delete user"));
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // Open delete user confirmation dialog
  const openDeleteUserDialog = (user) => {
    setDeletingUser(user);
    setDeleteConfirmText("");
    setDeleteUserDialogOpen(true);
  };

  // Available permissions list with clear descriptions
  const AVAILABLE_PERMISSIONS = [
    { 
      id: "create_subsidiary", 
      name: "Create Subsidiary", 
      category: "Company",
      description: "Create new subsidiaries within the company structure"
    },
    { 
      id: "manage_subsidiaries", 
      name: "Manage Subsidiaries", 
      category: "Company",
      description: "Edit, archive, or manage existing subsidiaries"
    },
    { 
      id: "assign_roles", 
      name: "Assign Roles", 
      category: "Users",
      description: "Assign system roles to company team members"
    },
    { 
      id: "view_user_roles", 
      name: "View User Roles", 
      category: "Users",
      description: "View which roles are assigned to team members"
    },
    { 
      id: "manage_users", 
      name: "Manage Users", 
      category: "Users",
      description: "Add, edit, or remove team members from the company"
    },
    { 
      id: "create_business_plan", 
      name: "Create Business Plan", 
      category: "Planning",
      description: "Create new business plans for subsidiaries (company planning, not system features)"
    },
    { 
      id: "edit_business_plan", 
      name: "Edit Business Plan", 
      category: "Planning",
      description: "Modify existing business plans (company data, not system settings)"
    },
    { 
      id: "view_executive_dashboard", 
      name: "View Executive Dashboard", 
      category: "Analytics",
      description: "Access company-wide executive summary dashboards"
    },
    { 
      id: "view_reports", 
      name: "View Reports", 
      category: "Analytics",
      description: "View financial and operational reports for assigned subsidiaries"
    },
    { 
      id: "export_data", 
      name: "Export Data", 
      category: "Analytics",
      description: "Export business plan data and reports to Excel/PDF"
    },
    { 
      id: "manage_subsidiary_data", 
      name: "Manage Subsidiary Data", 
      category: "Planning",
      description: "Full control over subsidiary's business planning data"
    },
    { 
      id: "view_all_data", 
      name: "View All Data", 
      category: "Analytics",
      description: "View data across all subsidiaries in the company"
    },
    { 
      id: "edit_financial_models", 
      name: "Edit Financial Models", 
      category: "Planning",
      description: "Modify financial projections and models in business plans"
    }
  ];

  // Handle create new role
  const handleCreateRole = async () => {
    if (!newRoleForm.name || newRoleForm.permissions.length === 0) {
      toast.error("Please enter role name and select at least one permission");
      return;
    }

    try {
      await axios.post(`${ENTERPRISE_API}/roles`, {
        ...newRoleForm,
        is_system_role: false,
        role_scope: "subsidiary"
      });

      toast.success("Role created successfully!");
      setCreateRoleDialogOpen(false);
      setNewRoleForm({ name: "", description: "", level: 5, permissions: [] });
      fetchAdminData();
    } catch (error) {
      console.error('Create role error:', error);
      toast.error(getErrorMessage(error, "Failed to create role"));
    }
  };

  // Handle edit role
  const openEditRole = (role) => {
    setEditingRole(role);
    setNewRoleForm({
      name: role.name,
      description: role.description || "",
      level: role.level,
      permissions: role.permissions || []
    });
    setEditRoleDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!newRoleForm.name || newRoleForm.permissions.length === 0) {
      toast.error("Please enter role name and select at least one permission");
      return;
    }

    try {
      await axios.put(`${ENTERPRISE_API}/roles/${editingRole.id}`, newRoleForm);

      toast.success("Role updated successfully!");
      setEditRoleDialogOpen(false);
      setEditingRole(null);
      setNewRoleForm({ name: "", description: "", level: 5, permissions: [] });
      fetchAdminData();
    } catch (error) {
      console.error('Update role error:', error);
      toast.error(getErrorMessage(error, "Failed to update role"));
    }
  };

  // Handle delete role
  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Delete role "${roleName}"? This cannot be undone!`)) {
      return;
    }

    try {
      await axios.delete(`${ENTERPRISE_API}/roles/${roleId}`);
      toast.success("Role deleted successfully!");
      fetchAdminData();
    } catch (error) {
      console.error('Delete role error:', error);
      toast.error(getErrorMessage(error, "Failed to delete role"));
    }
  };

  // Toggle permission in form
  const togglePermission = (permissionId) => {
    setNewRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // Temporarily disable admin check for testing
  // TODO: Re-enable after making beyemoges@gmail.com admin
  /*
  if (!user?.is_admin) {
    return (
      <div className="p-8">
        <Card className="glass-card shadow-soft">
          <CardContent className="p-12 text-center">
            <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Access Denied</h3>
            <p className="text-slate-600">You don't have administrator privileges.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  */

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Admin Dashboard
          </h1>
          <p className="text-slate-600">
            System overview and user management for TavPlans platform
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Link to="/admin/platform">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Settings2 className="h-4 w-4 mr-2" />
              Platform Management
            </Button>
          </Link>
          <Link to="/admin/whitelabel">
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Palette className="h-4 w-4 mr-2" />
              White-Label Management
            </Button>
          </Link>
          <Badge className="bg-red-100 text-red-700">
            <Shield className="h-4 w-4 mr-1" />
            Administrator
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-medium">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-medium">Active Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.active_users}</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-medium">Total Plans</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_plans}</p>
                </div>
                <div className="bg-purple-100 rounded-full p-3">
                  <FileSpreadsheet className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-medium">Inactive Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_users - stats.active_users}</p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <UserX className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Analytics Cards */}
      {revenueAnalytics && (
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Revenue Analytics</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="glass-card shadow-soft border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm font-medium">Monthly Recurring Revenue</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(revenueAnalytics.mrr)}</p>
                  </div>
                  <div className="bg-green-100 rounded-full p-3">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card shadow-soft border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm font-medium">Annual Recurring Revenue</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(revenueAnalytics.arr)}</p>
                  </div>
                  <div className="bg-blue-100 rounded-full p-3">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card shadow-soft border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm font-medium">Active Subscriptions</p>
                    <p className="text-2xl font-bold text-purple-700">{revenueAnalytics.active_subscriptions}</p>
                  </div>
                  <div className="bg-purple-100 rounded-full p-3">
                    <Crown className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card shadow-soft border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm font-medium">Avg Revenue per User</p>
                    <p className="text-2xl font-bold text-orange-700">
                      ${stats?.active_users > 0 ? (revenueAnalytics.mrr / stats.active_users).toFixed(0) : 0}
                    </p>
                  </div>
                  <div className="bg-orange-100 rounded-full p-3">
                    <Users className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Registration Trend */}
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg">User Registration Trend</CardTitle>
            <CardDescription>New user signups over the last 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getUserStats()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}`, 'New Users']}
                    labelStyle={{ color: '#475569' }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Platform Activity */}
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg">Platform Activity</CardTitle>
            <CardDescription>Overall system statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Activity className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-slate-700">User Engagement</span>
                </div>
                <Badge className="bg-green-100 text-green-700">High</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-slate-700">Growth Rate</span>
                </div>
                <Badge className="bg-blue-100 text-blue-700">+15%</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-slate-700">Plans per User</span>
                </div>
                <span className="font-medium text-slate-900">
                  {stats ? (stats.total_plans / stats.total_users).toFixed(1) : '0'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Subscription Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription Plans Management */}
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center">
                  <Settings className="h-5 w-5 mr-2 text-blue-600" />
                  Subscription Plans
                </CardTitle>
                <CardDescription>Manage subscription tiers and pricing</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <SubscriptionPlansManager 
              subscriptionTiers={subscriptionTiers}
              onRefresh={fetchAdminData}
            />
          </CardContent>
        </Card>

        {/* User Subscriptions Management */}
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Crown className="h-5 w-5 mr-2 text-purple-600" />
              User Subscriptions
            </CardTitle>
            <CardDescription>Manage individual user subscriptions</CardDescription>
          </CardHeader>
          <CardContent>
            <UserSubscriptionsManager 
              subscriptions={subscriptions}
              users={users}
              subscriptionTiers={subscriptionTiers}
              onRefresh={fetchAdminData}
            />
          </CardContent>
        </Card>
      </div>

      {/* Coupon Management */}
      <CouponManager />

      {/* Affiliate Management Quick Access */}
      <Card className="glass-card shadow-soft">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center">
                <Users className="h-5 w-5 mr-2 text-green-600" />
                Affiliate Program
              </CardTitle>
              <CardDescription>Manage affiliate partners and commissions</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link to="/admin/affiliate-tiers">
                  <Settings className="h-4 w-4 mr-2" />
                  Tier Settings
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/payouts">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Payouts
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/affiliates">
                  <Eye className="h-4 w-4 mr-2" />
                  Manage Affiliates
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {stats?.affiliates?.total || 0}
              </div>
              <div className="text-sm text-blue-600">Total Affiliates</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {stats?.affiliates?.pending || 0}
              </div>
              <div className="text-sm text-yellow-600">Pending Approval</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {stats?.affiliates?.conversions || 0}
              </div>
              <div className="text-sm text-green-600">Total Conversions</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                ${stats?.affiliates?.earnings || 0}
              </div>
              <div className="text-sm text-purple-600">Total Earnings</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content with Tabs */}
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            System Roles
          </TabsTrigger>
        </TabsList>

        {/* USERS TAB */}
        <TabsContent value="users">
          {/* Search and Filters */}
          <Card className="glass-card shadow-soft mb-6">
            <CardContent className="pt-6">
              {/* Row 1: Search and Main Filters */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                {/* Search Bar */}
                <div className="md:col-span-2">
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Input
                      id="search"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-4"
                    />
                  </div>
                </div>

                {/* Company Filter */}
                <div>
                  <Label htmlFor="company-filter">Company</Label>
                  <Select value={companyFilter} onValueChange={setCompanyFilter}>
                    <SelectTrigger id="company-filter">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {getUniqueCompanies().map(company => (
                        <SelectItem key={company} value={company}>
                          {company}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* System Role Filter - NEW */}
                <div>
                  <Label htmlFor="system-role-filter">System Role</Label>
                  <Select value={systemRoleFilter} onValueChange={setSystemRoleFilter}>
                    <SelectTrigger id="system-role-filter">
                      <SelectValue placeholder="All System Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All System Roles</SelectItem>
                      <SelectItem value="platform_admin">Platform Admins</SelectItem>
                      <SelectItem value="company_user">Company Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div>
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger id="status-filter">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Row 2: Company Role Filter - NEW */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="company-role-filter">Company Role</Label>
                  <Select value={companyRoleFilter} onValueChange={setCompanyRoleFilter}>
                    <SelectTrigger id="company-role-filter">
                      <SelectValue placeholder="All Company Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Company Roles</SelectItem>
                      <SelectItem value="Company Admin">Company Admin</SelectItem>
                      <SelectItem value="Subsidiary Manager">Subsidiary Manager</SelectItem>
                      <SelectItem value="Analyst">Analyst</SelectItem>
                      <SelectItem value="Viewer">Viewer</SelectItem>
                      <SelectItem value="Data Encoder">Data Encoder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3"></div>
              </div>
              
              {/* Results Count and View Toggle */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {getFilteredUsers().length} of {users.length} users
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">View:</Label>
                  <div className="flex border rounded-md">
                    <Button
                      variant={viewMode === "list" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("list")}
                      className="rounded-r-none"
                    >
                      <Activity className="h-4 w-4 mr-1" />
                      List
                    </Button>
                    <Button
                      variant={viewMode === "hierarchy" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("hierarchy")}
                      className="rounded-l-none"
                    >
                      <Building2 className="h-4 w-4 mr-1" />
                      By Company
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users Table - List View */}
          {viewMode === "list" && (
            <Card className="glass-card shadow-soft">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Users Management</CardTitle>
                    <CardDescription>All registered users and their status</CardDescription>
                  </div>
                  <div className="text-sm text-slate-500">
                    Showing {((userPage - 1) * usersPerPage) + 1} - {Math.min(userPage * usersPerPage, getFilteredUsers().length)} of {getFilteredUsers().length} users
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>System Role</TableHead>
                      <TableHead>Company Roles</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredUsers()
                      .slice((userPage - 1) * usersPerPage, userPage * usersPerPage)
                      .map((user) => {
                      const userRoleData = userRoles[user.id] || {};
                      const companyRolesData = userRoleData.company_roles || [];
                      const subsidiaryRolesData = userRoleData.subsidiary_roles || [];
                      const allUserRoles = [...companyRolesData, ...subsidiaryRolesData];
                      
                      return (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{user.full_name}</p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-slate-700">
                              {user.company_name || 'Not specified'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={user.is_active ? "default" : "secondary"}
                              className={user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                            >
                              {user.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={user.is_admin ? "default" : "secondary"}
                              className={user.is_admin ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}
                            >
                              {user.is_admin ? "Platform Admin" : "Company User"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {allUserRoles.length > 0 ? (
                                <>
                                  {allUserRoles.slice(0, 2).map((role, idx) => (
                                    <RoleBadge 
                                      key={idx}
                                      role={{
                                        name: role.role_name,
                                        scope: role.role_scope,
                                        level: role.role_level,
                                        subsidiary_name: role.subsidiary_name,
                                        role_scope: role.role_scope
                                      }}
                                      size="sm"
                                    />
                                  ))}
                                  {allUserRoles.length > 2 && (
                                    <Badge variant="outline" className="text-xs bg-slate-50">
                                      +{allUserRoles.length - 2} more
                                    </Badge>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-slate-400">No roles assigned</span>
                              )}
                            </div>
                          </TableCell>
                        <TableCell>
                          <div className="flex items-center text-slate-600">
                            <Calendar className="h-4 w-4 mr-2" />
                            {formatDate(user.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openViewUserDialog(user)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>View Details</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openEditUserDialog(user)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Edit User</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openChangePasswordDialog(user)}
                                  >
                                    <Shield className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Change Password</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openRoleDialog(user)}
                                  >
                                    <Settings className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Assign Role</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant={user.is_admin ? "destructive" : "default"}
                                    size="sm"
                                    onClick={() => handleToggleAdminStatus(user.id, user.is_admin)}
                                  >
                                    {user.is_admin ? <UserX className="h-4 w-4" /> : <Crown className="h-4 w-4" />}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{user.is_admin ? "Remove Admin" : "Make Admin"}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => openDeleteUserDialog(user)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Delete User</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {getFilteredUsers().length > usersPerPage && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm text-slate-500">
                      Page {userPage} of {Math.ceil(getFilteredUsers().length / usersPerPage)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUserPage(p => Math.max(1, p - 1))}
                        disabled={userPage === 1}
                      >
                        Previous
                      </Button>
                      {Array.from({ length: Math.min(5, Math.ceil(getFilteredUsers().length / usersPerPage)) }, (_, i) => {
                        const totalPages = Math.ceil(getFilteredUsers().length / usersPerPage);
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (userPage <= 3) {
                          pageNum = i + 1;
                        } else if (userPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = userPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={userPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setUserPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUserPage(p => Math.min(Math.ceil(getFilteredUsers().length / usersPerPage), p + 1))}
                        disabled={userPage >= Math.ceil(getFilteredUsers().length / usersPerPage)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Users Table - Hierarchy View */}
          {viewMode === "hierarchy" && (
            <Card className="glass-card shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Users by Company</CardTitle>
                <CardDescription>Users organized by their companies</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(getGroupedUsers()).map(([companyName, companyUsers]) => (
                    <div key={companyName} className="border rounded-lg p-4">
                      {/* Company Header */}
                      <div className="flex items-center justify-between mb-4 pb-3 border-b">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-blue-600" />
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900">{companyName}</h3>
                            <p className="text-sm text-slate-500">{companyUsers.length} user{companyUsers.length !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-sm">
                          {companyUsers.length} {companyUsers.length === 1 ? 'User' : 'Users'}
                        </Badge>
                      </div>

                      {/* Company Users Table */}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>System Role</TableHead>
                            <TableHead>Company Roles</TableHead>
                            <TableHead>Joined</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {companyUsers.map((user) => {
                            const userRoleData = userRoles[user.id] || {};
                            const companyRolesData = userRoleData.company_roles || [];
                            const subsidiaryRolesData = userRoleData.subsidiary_roles || [];
                            const allUserRoles = [...companyRolesData, ...subsidiaryRolesData];
                            
                            return (
                              <TableRow key={user.id}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-slate-900">{user.full_name}</p>
                                    <p className="text-sm text-slate-500">{user.email}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant={user.is_active ? "default" : "secondary"}
                                    className={user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                                  >
                                    {user.is_active ? "Active" : "Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant={user.is_admin ? "default" : "secondary"}
                                    className={user.is_admin ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}
                                  >
                                    {user.is_admin ? "Platform Admin" : "Company User"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {allUserRoles.length > 0 ? (
                                      <>
                                        {allUserRoles.slice(0, 2).map((role, idx) => (
                                          <RoleBadge 
                                            key={idx}
                                            role={{
                                              name: role.role_name,
                                              scope: role.role_scope,
                                              level: role.role_level,
                                              subsidiary_name: role.subsidiary_name,
                                              role_scope: role.role_scope
                                            }}
                                            size="sm"
                                          />
                                        ))}
                                        {allUserRoles.length > 2 && (
                                          <Badge variant="outline" className="text-xs bg-slate-50">
                                            +{allUserRoles.length - 2} more
                                          </Badge>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-sm text-slate-400">No roles assigned</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center text-slate-600">
                                    <Calendar className="h-4 w-4 mr-2" />
                                    {formatDate(user.created_at)}
                                  </div>
                                </TableCell>
                              <TableCell>
                                <div className="flex space-x-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openViewUserDialog(user)}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openRoleDialog(user)}
                                  >
                                    <Settings className="h-4 w-4 mr-2" />
                                    Assign Role
                                  </Button>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant={user.is_admin ? "destructive" : "default"}
                                          size="sm"
                                          onClick={() => handleToggleAdminStatus(user.id, user.is_admin)}
                                        >
                                          {user.is_admin ? <UserX className="h-4 w-4" /> : <Crown className="h-4 w-4" />}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{user.is_admin ? "Remove Admin" : "Make Admin"}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                  
                  {Object.keys(getGroupedUsers()).length === 0 && (
                    <div className="text-center py-12">
                      <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                      <p className="text-gray-500">Try adjusting your filters</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* COMPANIES TAB */}
        <TabsContent value="companies">
          {/* Company Search */}
          <Card className="glass-card shadow-soft mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="company-search">Search Companies</Label>
                  <Input
                    id="company-search"
                    placeholder="Search by company name or owner email..."
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="text-sm text-gray-600 pt-6">
                  {getFilteredCompanies().length} companies found
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Companies Table */}
          <Card className="glass-card shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg">Companies Management</CardTitle>
              <CardDescription>All companies using the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-center">Subsidiaries</TableHead>
                    <TableHead className="text-center">Users</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredCompanies().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
                        <p className="text-gray-500">Try adjusting your search</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getPaginatedCompanies().map((company, index) => (
                      <TableRow key={company.id || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-slate-900">{company.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">{company.owner?.name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{company.owner?.email}</p>
                            {company.owner?.is_admin && (
                              <Badge variant="outline" className="mt-1 text-xs bg-red-50 text-red-600 border-red-200">
                                Platform Admin
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col gap-1">
                            {company.subsidiaries?.active > 0 && (
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                {company.subsidiaries.active} Active
                              </Badge>
                            )}
                            {company.subsidiaries?.inactive > 0 && (
                              <Badge variant="outline" className="bg-gray-50 text-gray-600">
                                {company.subsidiaries.inactive} Inactive
                              </Badge>
                            )}
                            {(!company.subsidiaries || (company.subsidiaries.active === 0 && company.subsidiaries.inactive === 0)) && (
                              <Badge variant="outline" className="text-gray-400">0</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {company.users?.total || 0} Total
                            </Badge>
                            {company.users && (
                              <div className="text-xs text-gray-500">
                                {company.users.platform_admins > 0 && `${company.users.platform_admins} Admin${company.users.platform_admins > 1 ? 's' : ''}`}
                                {company.users.company_admins > 0 && `, ${company.users.company_admins} Co.Admin`}
                                {company.users.subsidiary_managers > 0 && `, ${company.users.subsidiary_managers} Mgr`}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" className="bg-green-100 text-green-700">
                            {company.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-slate-600">
                            <Calendar className="h-4 w-4 mr-2" />
                            {formatDate(company.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openCompanyDetails(company)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteCompany(company.name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {getTotalCompanyPages() > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {((companyPage - 1) * companiesPerPage) + 1} to {Math.min(companyPage * companiesPerPage, getFilteredCompanies().length)} of {getFilteredCompanies().length} companies
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCompanyPage(prev => Math.max(1, prev - 1))}
                      disabled={companyPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: getTotalCompanyPages() }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={page === companyPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCompanyPage(page)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCompanyPage(prev => Math.min(getTotalCompanyPages(), prev + 1))}
                      disabled={companyPage === getTotalCompanyPages()}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SYSTEM ROLES TAB */}
        <TabsContent value="roles">
          {/* Header with Create Button */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold">System Roles Management</h2>
              <p className="text-gray-600">Create and manage platform roles</p>
            </div>
            <Button onClick={() => setCreateRoleDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create New Role
            </Button>
          </div>

          {/* Roles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map((role) => (
              <Card key={role.id} className="glass-card shadow-soft">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-blue-600" />
                        {role.name}
                      </CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge variant={role.is_system_role ? "default" : "outline"}>
                          {role.is_system_role ? "System Role" : "Custom Role"}
                        </Badge>
                        <Badge variant="outline" title="Lower level = Higher authority">
                          Level {role.level}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4" title="Role purpose and scope">
                    {role.description || "No description"}
                  </p>

                  {/* Permissions List */}
                  <div className="mb-4">
                    <Label className="text-xs font-medium text-gray-500 uppercase">
                      Permissions ({role.permissions?.length || 0})
                    </Label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(role.permissions || []).slice(0, 4).map((perm) => {
                        const permDetail = AVAILABLE_PERMISSIONS.find(p => p.id === perm);
                        return (
                          <Badge 
                            key={perm} 
                            variant="secondary" 
                            className="text-xs cursor-help"
                            title={permDetail?.description || perm}
                          >
                            {perm.replace(/_/g, ' ')}
                          </Badge>
                        );
                      })}
                      {role.permissions?.length > 4 && (
                        <Badge variant="secondary" className="text-xs">
                          +{role.permissions.length - 4} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEditRole(role)}
                      title={role.is_system_role ? "Platform admins can edit system roles" : "Edit this custom role"}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {!role.is_system_role && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRole(role.id, role.name)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete this custom role"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {roles.length === 0 && (
            <Card className="glass-card shadow-soft">
              <CardContent className="text-center py-12">
                <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No roles found</h3>
                <p className="text-gray-500 mb-4">Create your first role to get started</p>
                <Button onClick={() => setCreateRoleDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Role
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Users Table */}
      {/* REMOVED - Now inside Users tab above */}

      {/* Role Assignment Dialog - REDESIGNED */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage User Roles</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              {/* User Info Section */}
              <Card className="border-2">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">User:</p>
                      <p className="font-medium text-lg">{selectedUser.full_name}</p>
                      <p className="text-sm text-gray-500">{selectedUser.email}</p>
                      <p className="text-xs text-gray-400 mt-1">Company: {selectedUser.company_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <Badge 
                        variant={selectedUser.is_admin ? "default" : "secondary"}
                        className={selectedUser.is_admin ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}
                      >
                        {selectedUser.is_admin ? "Platform Admin" : "Company User"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Tabs for Assigned Roles and Assign New */}
              <Tabs value={roleDialogTab} onValueChange={setRoleDialogTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="assigned">Assigned Roles</TabsTrigger>
                  <TabsTrigger value="assign">Assign New Role</TabsTrigger>
                </TabsList>
                
                {/* TAB 1: Assigned Roles */}
                <TabsContent value="assigned" className="space-y-4 mt-4">
                  {selectedUserRoles ? (
                    <>
                      {/* Info Note */}
                      {selectedUser.is_admin && (
                        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-gray-700">
                          <Crown className="h-4 w-4 inline mr-1 text-red-600" />
                          This user has <strong>Platform Admin</strong> privileges (system-wide access). Use the crown icon toggle to change this.
                        </div>
                      )}
                      
                      {/* Company Roles Section */}
                      {selectedUserRoles.company_roles && selectedUserRoles.company_roles.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Company-Wide Roles
                          </h3>
                          <div className="space-y-2">
                            {selectedUserRoles.company_roles.map((role) => (
                              <Card key={role.id} className="border">
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <RoleBadge 
                                          role={{
                                            name: role.role_name,
                                            scope: role.role_scope,
                                            level: role.role_level,
                                            role_scope: role.role_scope
                                          }}
                                          size="md"
                                        />
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        Scope: Company-wide ({selectedUser.company_name})
                                      </p>
                                      <p className="text-xs text-gray-400 mt-1">
                                        Assigned: {new Date(role.granted_at).toLocaleDateString()} by {role.granted_by_name}
                                      </p>
                                      {role.permissions && role.permissions.length > 0 && (
                                        <details className="mt-2">
                                          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-700">
                                            View {role.permissions.length} permissions
                                          </summary>
                                          <div className="mt-2 flex flex-wrap gap-1">
                                            {role.permissions.slice(0, 6).map((perm, idx) => (
                                              <Badge key={idx} variant="outline" className="text-xs">
                                                {perm.replace(/_/g, ' ')}
                                              </Badge>
                                            ))}
                                            {role.permissions.length > 6 && (
                                              <Badge variant="outline" className="text-xs">
                                                +{role.permissions.length - 6} more
                                              </Badge>
                                            )}
                                          </div>
                                        </details>
                                      )}
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRemoveRole(role.id, role.role_name)}
                                      disabled={removingRoleId === role.id}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4 mr-1" />
                                      Remove
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Subsidiary Roles Section */}
                      {selectedUserRoles.subsidiary_roles && selectedUserRoles.subsidiary_roles.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            Subsidiary-Specific Roles
                          </h3>
                          <div className="space-y-2">
                            {selectedUserRoles.subsidiary_roles.map((role) => (
                              <Card key={role.id} className="border">
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <RoleBadge 
                                          role={{
                                            name: role.role_name,
                                            scope: role.role_scope,
                                            level: role.role_level,
                                            subsidiary_name: role.subsidiary_name,
                                            role_scope: role.role_scope
                                          }}
                                          size="md"
                                        />
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        Scope: {role.subsidiary_name} ({role.subsidiary_code})
                                      </p>
                                      <p className="text-xs text-gray-400 mt-1">
                                        Assigned: {new Date(role.granted_at).toLocaleDateString()} by {role.granted_by_name}
                                      </p>
                                      {role.permissions && role.permissions.length > 0 && (
                                        <details className="mt-2">
                                          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-700">
                                            View {role.permissions.length} permissions
                                          </summary>
                                          <div className="mt-2 flex flex-wrap gap-1">
                                            {role.permissions.slice(0, 6).map((perm, idx) => (
                                              <Badge key={idx} variant="outline" className="text-xs">
                                                {perm.replace(/_/g, ' ')}
                                              </Badge>
                                            ))}
                                            {role.permissions.length > 6 && (
                                              <Badge variant="outline" className="text-xs">
                                                +{role.permissions.length - 6} more
                                              </Badge>
                                            )}
                                          </div>
                                        </details>
                                      )}
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRemoveRole(role.id, role.role_name)}
                                      disabled={removingRoleId === role.id}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4 mr-1" />
                                      Remove
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* No Roles Assigned */}
                      {(!selectedUserRoles.company_roles || selectedUserRoles.company_roles.length === 0) && 
                       (!selectedUserRoles.subsidiary_roles || selectedUserRoles.subsidiary_roles.length === 0) && (
                        <Card className="border-dashed border-2">
                          <CardContent className="text-center py-12">
                            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <h3 className="text-sm font-medium text-gray-900 mb-1">No Company Roles Assigned</h3>
                            <p className="text-xs text-gray-500 mb-4">
                              This user has no company or subsidiary roles assigned yet.
                            </p>
                            <Button
                              size="sm"
                              onClick={() => setRoleDialogTab("assign")}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Assign First Role
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Quick Assign Button */}
                      {(selectedUserRoles.company_roles?.length > 0 || selectedUserRoles.subsidiary_roles?.length > 0) && (
                        <div className="flex justify-center pt-2">
                          <Button
                            variant="outline"
                            onClick={() => setRoleDialogTab("assign")}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Assign Another Role
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-pulse text-gray-500">Loading roles...</div>
                    </div>
                  )}
                </TabsContent>
                
                {/* TAB 2: Assign New Role */}
                <TabsContent value="assign" className="space-y-4 mt-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Role to Assign</h3>
                    
                    <p className="text-xs text-gray-500 mb-4 bg-blue-50 p-3 rounded border border-blue-200">
                      <Shield className="h-4 w-4 inline mr-1" />
                      <strong>Note:</strong> Super Admin is a platform-wide role managed via the &quot;Toggle Admin&quot; button (crown icon), not assigned here.
                    </p>
                    
                    {/* Company & Subsidiary Roles - Combined */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Company & Subsidiary Roles
                      </h4>
                      <div className="space-y-2">
                        {roles.filter(r => r.role_scope !== "platform").map(role => {
                          // Check if already assigned (for company roles)
                          const alreadyAssigned = role.role_scope === "company" 
                            ? selectedUserRoles?.company_roles?.some(ur => ur.role_id === role.id)
                            : false;
                          
                          return (
                            <Card 
                              key={role.id} 
                              className={`border cursor-pointer transition-all ${
                                selectedRole === role.id 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : alreadyAssigned 
                                    ? 'opacity-50 border-dashed' 
                                    : 'hover:border-blue-300'
                              }`}
                              onClick={() => !alreadyAssigned && setSelectedRole(role.id)}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <RoleBadge 
                                        role={{
                                          name: role.name,
                                          scope: role.role_scope,
                                          level: role.level,
                                          role_scope: role.role_scope
                                        }}
                                        size="md"
                                      />
                                      {alreadyAssigned && (
                                        <Badge variant="secondary" className="text-xs">Already Assigned</Badge>
                                      )}
                                      {role.role_scope === "company" && (
                                        <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">
                                          Company-Wide
                                        </Badge>
                                      )}
                                      {role.role_scope === "subsidiary" && (
                                        <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">
                                          Subsidiary-Specific
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">{role.description}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Level {role.level} • {role.permissions?.length || 0} permissions
                                    </p>
                                  </div>
                                  {!alreadyAssigned && selectedRole === role.id && (
                                    <CheckCircle className="h-5 w-5 text-blue-600" />
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Subsidiary Selector - Show only if subsidiary role selected */}
                    {selectedRole && roles.find(r => r.id === selectedRole)?.role_scope === "subsidiary" && (
                      <div className="mt-4">
                        <Label htmlFor="subsidiary-select">Select Subsidiary *</Label>
                        <Select value={selectedSubsidiary || ""} onValueChange={setSelectedSubsidiary}>
                          <SelectTrigger id="subsidiary-select">
                            <SelectValue placeholder="Choose a subsidiary" />
                          </SelectTrigger>
                          <SelectContent>
                            {subsidiaries.map(sub => (
                              <SelectItem key={sub.id} value={sub.id}>
                                {sub.name} ({sub.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          This role will only apply to the selected subsidiary
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedRole("");
                        setSelectedSubsidiary("");
                        setRoleDialogTab("assigned");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleAssignRole}
                      disabled={!selectedRole}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Assign Role
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
              
              {/* Close Button */}
              <div className="flex justify-end pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRoleDialogOpen(false);
                    setSelectedUser(null);
                    setSelectedRole("");
                    setSelectedSubsidiary("");
                    setSelectedUserRoles(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View User Details Dialog */}
      <Dialog open={viewUserDialogOpen} onOpenChange={setViewUserDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {viewingUser && (
            <div className="space-y-6">
              {/* User Info Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Full Name</Label>
                  <p className="text-base font-medium mt-1">{viewingUser.full_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Email</Label>
                  <p className="text-base mt-1">{viewingUser.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Company</Label>
                  <p className="text-base mt-1">{viewingUser.company_name || 'Not specified'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Status</Label>
                  <div className="mt-1">
                    <Badge 
                      variant={viewingUser.is_active ? "default" : "secondary"}
                      className={viewingUser.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                    >
                      {viewingUser.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Account Type</Label>
                  <div className="mt-1">
                    <Badge 
                      variant={viewingUser.is_admin ? "default" : "secondary"}
                      className={viewingUser.is_admin ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}
                    >
                      {viewingUser.is_admin ? "Administrator" : "User"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Joined</Label>
                  <p className="text-base mt-1">{formatDate(viewingUser.created_at)}</p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-sm mb-3">Additional Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">User ID:</span>
                    <p className="font-mono text-xs mt-1">{viewingUser.id}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Updated:</span>
                    <p className="text-xs mt-1">{viewingUser.updated_at ? formatDate(viewingUser.updated_at) : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setViewUserDialogOpen(false);
                    setViewingUser(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setViewUserDialogOpen(false);
                    openRoleDialog(viewingUser);
                  }}
                >
                  Assign Role
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Company Details Dialog */}
      <Dialog open={viewCompanyDetailsOpen} onOpenChange={setViewCompanyDetailsOpen}>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information for {editingUser?.full_name || editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={editUserForm.email}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={editUserForm.full_name}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={editUserForm.company_name}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Company Inc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditUserSubmit} disabled={editUserLoading}>
              {editUserLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={(open) => {
        setDeleteUserDialogOpen(open);
        if (!open) {
          setDeletingUser(null);
          setDeleteConfirmText("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete User Permanently
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              This action cannot be undone. This will permanently delete the user account and all associated data.
            </DialogDescription>
          </DialogHeader>
          
          {deletingUser && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">You are about to delete:</p>
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-600">Name:</span> <span className="font-medium text-slate-900">{deletingUser.full_name || 'N/A'}</span></p>
                  <p><span className="text-slate-600">Email:</span> <span className="font-medium text-slate-900">{deletingUser.email}</span></p>
                  <p><span className="text-slate-600">Company:</span> <span className="font-medium text-slate-900">{deletingUser.company_name || 'N/A'}</span></p>
                  <p><span className="text-slate-600">Role:</span> <span className="font-medium text-slate-900">{deletingUser.is_admin ? 'Platform Admin' : 'Company User'}</span></p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deleteConfirm" className="text-sm font-medium">
                  To confirm deletion, type: <code className="bg-slate-100 px-2 py-1 rounded text-red-600 font-mono">DELETE {deletingUser.email}</code>
                </Label>
                <Input
                  id="deleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={`DELETE ${deletingUser.email}`}
                  className="font-mono"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteUserDialogOpen(false);
                setDeletingUser(null);
                setDeleteConfirmText("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteUserLoading || deleteConfirmText !== `DELETE ${deletingUser?.email}`}
            >
              {deleteUserLoading ? (
                "Deleting..."
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User Forever
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onOpenChange={setChangePasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Set a new password for {passwordUser?.full_name || passwordUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-500">Passwords do not match</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={changePasswordLoading || newPassword.length < 8 || newPassword !== confirmPassword}
            >
              {changePasswordLoading ? "Changing..." : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Company Details: {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-6">
              {/* Company Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs font-medium text-gray-500">Company Name</Label>
                  <p className="text-sm font-medium mt-1">{selectedCompany.name}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Owner</Label>
                  <p className="text-sm mt-1">{selectedCompany.owner?.name || 'N/A'}</p>
                  <p className="text-xs text-gray-500">{selectedCompany.owner?.email}</p>
                  {selectedCompany.owner?.is_admin && (
                    <Badge variant="outline" className="mt-1 text-xs bg-red-50 text-red-600 border-red-200">
                      Platform Admin
                    </Badge>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Total Users</Label>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{selectedCompany.users?.total || 0}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Subsidiaries</Label>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {selectedCompany.subsidiaries?.active || 0}
                    {selectedCompany.subsidiaries?.inactive > 0 && (
                      <span className="text-sm text-gray-500"> (+{selectedCompany.subsidiaries.inactive} inactive)</span>
                    )}
                  </p>
                </div>
              </div>

              {/* User Distribution */}
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  User Distribution ({selectedCompany.users?.total || 0} Total)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {selectedCompany.users?.platform_admins > 0 && (
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Platform Admins</p>
                            <p className="text-2xl font-bold text-red-600">{selectedCompany.users.platform_admins}</p>
                          </div>
                          <Crown className="h-8 w-8 text-red-300" />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {selectedCompany.users?.company_admins > 0 && (
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Company Admins</p>
                            <p className="text-2xl font-bold text-blue-600">{selectedCompany.users.company_admins}</p>
                          </div>
                          <Building2 className="h-8 w-8 text-blue-300" />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {selectedCompany.users?.subsidiary_managers > 0 && (
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Subsidiary Managers</p>
                            <p className="text-2xl font-bold text-purple-600">{selectedCompany.users.subsidiary_managers}</p>
                          </div>
                          <Briefcase className="h-8 w-8 text-purple-300" />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {selectedCompany.users?.analysts > 0 && (
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Analysts</p>
                            <p className="text-2xl font-bold text-green-600">{selectedCompany.users.analysts}</p>
                          </div>
                          <Activity className="h-8 w-8 text-green-300" />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {selectedCompany.users?.viewers > 0 && (
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Viewers</p>
                            <p className="text-2xl font-bold text-gray-600">{selectedCompany.users.viewers}</p>
                          </div>
                          <Eye className="h-8 w-8 text-gray-300" />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {selectedCompany.users?.other > 0 && (
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500">Other Roles</p>
                            <p className="text-2xl font-bold text-gray-600">{selectedCompany.users.other}</p>
                          </div>
                          <Users className="h-8 w-8 text-gray-300" />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Subsidiaries Info */}
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Subsidiaries ({(selectedCompany.subsidiaries?.total || 0)})
                </h4>
                {(selectedCompany.subsidiaries?.total || 0) === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center border rounded-lg">
                    No subsidiaries found for this company
                  </p>
                ) : (
                  <div className="text-sm text-gray-700 py-4 px-4 border rounded-lg bg-gray-50">
                    <p className="mb-2">
                      <span className="font-semibold">{selectedCompany.subsidiaries.total}</span> subsidiaries associated with this company:
                    </p>
                    <ul className="space-y-1 ml-4">
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span><strong>{selectedCompany.subsidiaries.active}</strong> Active</span>
                      </li>
                      {selectedCompany.subsidiaries.inactive > 0 && (
                        <li className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                          <span><strong>{selectedCompany.subsidiaries.inactive}</strong> Inactive</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setViewCompanyDetailsOpen(false);
                    setSelectedCompany(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={createRoleDialogOpen} onOpenChange={setCreateRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New System Role</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="role-name">Role Name *</Label>
              <Input
                id="role-name"
                value={newRoleForm.name}
                onChange={(e) => setNewRoleForm({...newRoleForm, name: e.target.value})}
                placeholder="e.g., Project Manager"
              />
            </div>

            <div>
              <Label htmlFor="role-description">Description</Label>
              <Textarea
                id="role-description"
                value={newRoleForm.description}
                onChange={(e) => setNewRoleForm({...newRoleForm, description: e.target.value})}
                placeholder="Describe the role's purpose..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="role-level">Hierarchy Level (1-10)</Label>
              <Input
                id="role-level"
                type="number"
                min="1"
                max="10"
                value={newRoleForm.level}
                onChange={(e) => setNewRoleForm({...newRoleForm, level: parseInt(e.target.value)})}
              />
              <p className="text-xs text-gray-500 mt-1">Lower number = Higher authority</p>
            </div>

            <div>
              <Label>Permissions * (Select at least one)</Label>
              <TooltipProvider>
                <div className="border rounded-lg p-4 mt-2 max-h-64 overflow-y-auto">
                  {Object.entries(
                    AVAILABLE_PERMISSIONS.reduce((acc, perm) => {
                      if (!acc[perm.category]) acc[perm.category] = [];
                      acc[perm.category].push(perm);
                      return acc;
                    }, {})
                  ).map(([category, perms]) => (
                    <div key={category} className="mb-4">
                      <h4 className="font-medium text-sm mb-2 text-gray-700">{category}</h4>
                      <div className="space-y-2">
                        {perms.map((perm) => (
                          <Tooltip key={`create-${perm.id}`} delayDuration={300}>
                            <TooltipTrigger asChild>
                              <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={newRoleForm.permissions.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  className="rounded"
                                />
                                <span className="text-sm font-medium">{perm.name}</span>
                              </label>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-gray-900 text-white">
                              <p>{perm.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TooltipProvider>
              <p className="text-xs text-gray-500 mt-2">
                Selected: {newRoleForm.permissions.length} permission(s)
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateRoleDialogOpen(false);
                  setNewRoleForm({ name: "", description: "", level: 5, permissions: [] });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateRole}>
                Create Role
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onOpenChange={setEditRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Role: {editingRole?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-role-name">Role Name *</Label>
              <Input
                id="edit-role-name"
                value={newRoleForm.name}
                onChange={(e) => setNewRoleForm({...newRoleForm, name: e.target.value})}
                placeholder="e.g., Project Manager"
              />
            </div>

            <div>
              <Label htmlFor="edit-role-description">Description</Label>
              <Textarea
                id="edit-role-description"
                value={newRoleForm.description}
                onChange={(e) => setNewRoleForm({...newRoleForm, description: e.target.value})}
                placeholder="Describe the role's purpose..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="edit-role-level">Hierarchy Level (1-10)</Label>
              <Input
                id="edit-role-level"
                type="number"
                min="1"
                max="10"
                value={newRoleForm.level}
                onChange={(e) => setNewRoleForm({...newRoleForm, level: parseInt(e.target.value)})}
              />
              <p className="text-xs text-gray-500 mt-1">Lower number = Higher authority</p>
            </div>

            <div>
              <Label>Permissions * (Select at least one)</Label>
              <TooltipProvider>
                <div className="border rounded-lg p-4 mt-2 max-h-64 overflow-y-auto">
                  {Object.entries(
                    AVAILABLE_PERMISSIONS.reduce((acc, perm) => {
                      if (!acc[perm.category]) acc[perm.category] = [];
                      acc[perm.category].push(perm);
                      return acc;
                    }, {})
                  ).map(([category, perms]) => (
                    <div key={category} className="mb-4">
                      <h4 className="font-medium text-sm mb-2 text-gray-700">{category}</h4>
                      <div className="space-y-2">
                        {perms.map((perm) => (
                          <Tooltip key={`edit-${perm.id}`} delayDuration={300}>
                            <TooltipTrigger asChild>
                              <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={newRoleForm.permissions.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  className="rounded"
                                />
                                <span className="text-sm font-medium">{perm.name}</span>
                              </label>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-gray-900 text-white">
                              <p>{perm.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TooltipProvider>
              <p className="text-xs text-gray-500 mt-2">
                Selected: {newRoleForm.permissions.length} permission(s)
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setEditRoleDialogOpen(false);
                  setEditingRole(null);
                  setNewRoleForm({ name: "", description: "", level: 5, permissions: [] });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateRole}>
                Update Role
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Create/Edit Plan Form Component
const CreateEditPlanForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isEditing 
}) => {
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-populate features and suggested pricing when tier name changes
      if (field === 'name' && TIER_HIERARCHY[value?.toLowerCase()]) {
        const tierKey = value.toLowerCase();
        const tierFeatures = getFeaturesForTier(tierKey);
        updated.features = tierFeatures;
        
        // Suggested pricing based on tier
        const suggestedPricing = {
          starter: { monthly: 0, annual: 0 },
          professional: { monthly: 49, annual: 490 },
          business: { monthly: 149, annual: 1490 },
          enterprise: { monthly: 399, annual: 3990 }
        };
        
        if (suggestedPricing[tierKey] && !isEditing) {
          updated.price_monthly = suggestedPricing[tierKey].monthly;
          updated.price_annual = suggestedPricing[tierKey].annual;
        }
      }
      
      return updated;
    });
  };

  const handleLimitsChange = (limitField, value) => {
    setFormData(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [limitField]: parseInt(value) || 0
      }
    }));
  };

  const getFeatureDisplayName = (featureKey) => {
    const featureMap = {
      'basic_planning': 'Basic Financial Planning',
      'advanced_planning': 'Advanced Financial Planning',
      'single_business_plan': 'Single Business Plan',
      'multiple_business_plans': 'Multiple Business Plans',
      'unlimited_business_plans': 'Unlimited Business Plans',
      'scenario_modeling': 'Scenario Modeling & What-If Analysis',
      'cost_center_management': 'Cost Center Management',
      'business_intelligence': 'Business Intelligence & Reports',
      'strategic_framework': 'Strategic Framework',
      'performance_tracking': 'Performance Tracking',
      'financial_statements': 'Financial Statements',
      'cash_flow_analysis': 'Cash Flow Analysis',
      'roi_analysis': 'ROI Analysis',
      'break_even_analysis': 'Break-Even Analysis',
      'export_reports': 'Export Reports (PDF/Excel)',
      'multi_currency': 'Multi-Currency Support',
      'team_collaboration': 'Team Collaboration',
      'subsidiaries_management': 'Subsidiaries Management',
      'role_based_access': 'Role-Based Access Control',
      'api_access': 'API Access',
      'priority_support': 'Priority Support',
      'phone_support': 'Phone Support',
      'custom_branding': 'Custom Branding',
      'white_label': 'White Label Solution',
      'data_import_export': 'Data Import/Export',
      'integrations': 'Third-Party Integrations',
      'audit_trail': 'Audit Trail',
      'compliance_reports': 'Compliance Reports'
    };
    return featureMap[featureKey] || featureKey;
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Plan Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., professional"
          />
        </div>

        <div>
          <Label htmlFor="display_name">Display Name *</Label>
          <Input
            id="display_name"
            value={formData.display_name}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            placeholder="e.g., Professional Plan"
          />
        </div>
        
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Brief description of the plan features"
            rows={2}
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-4">
        <h4 className="font-medium">Pricing</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price_monthly">Monthly Price ($) *</Label>
            <Input
              id="price_monthly"
              type="number"
              min="0"
              step="0.01"
              value={formData.price_monthly}
              onChange={(e) => handleInputChange('price_monthly', parseFloat(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label htmlFor="price_annual">Annual Price ($) *</Label>
            <Input
              id="price_annual"
              type="number"
              min="0"
              step="0.01"
              value={formData.price_annual}
              onChange={(e) => handleInputChange('price_annual', parseFloat(e.target.value) || 0)}
            />
          </div>
        </div>
      </div>

      {/* Enhanced Features Management */}
      <div className="space-y-4">
        <EnhancedFeatureSelector
          selectedTier={formData.name || 'starter'}
          selectedFeatures={formData.features}
          onFeaturesChange={(features) => setFormData(prev => ({ ...prev, features }))}
          editMode={true}
        />
      </div>

      {/* Limits */}
      <div className="space-y-4">
        <h4 className="font-medium">Plan Limits</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="business_plans">Business Plans</Label>
            <Input
              id="business_plans"
              type="number"
              min="-1"
              value={formData.limits.business_plans}
              onChange={(e) => handleLimitsChange('business_plans', e.target.value)}
              placeholder="-1 for unlimited"
            />
            <p className="text-xs text-gray-500 mt-1">-1 for unlimited</p>
          </div>
          <div>
            <Label htmlFor="subsidiaries">Subsidiaries</Label>
            <Input
              id="subsidiaries"
              type="number"
              min="-1"
              value={formData.limits.subsidiaries}
              onChange={(e) => handleLimitsChange('subsidiaries', e.target.value)}
              placeholder="-1 for unlimited"
            />
          </div>
          <div>
            <Label htmlFor="users">Team Members</Label>
            <Input
              id="users"
              type="number"
              min="-1"
              value={formData.limits.users}
              onChange={(e) => handleLimitsChange('users', e.target.value)}
              placeholder="-1 for unlimited"
            />
          </div>
          <div>
            <Label htmlFor="scenarios">Scenarios</Label>
            <Input
              id="scenarios"
              type="number"
              min="-1"
              value={formData.limits.scenarios}
              onChange={(e) => handleLimitsChange('scenarios', e.target.value)}
              placeholder="-1 for unlimited"
            />
          </div>
        </div>
      </div>

      {/* Active Status */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => handleInputChange('is_active', e.target.checked)}
        />
        <Label htmlFor="is_active">Plan is active</Label>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          onClick={onSubmit}
          disabled={!formData.name || !formData.display_name || formData.price_monthly < 0 || formData.price_annual < 0}
        >
          {isEditing ? 'Update Plan' : 'Create Plan'}
        </Button>
      </div>
    </div>
  );
};

// Subscription Plans Manager Component - Redesigned for Pricing Page Management
const SubscriptionPlansManager = ({ subscriptionTiers, onRefresh }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [systemFeatures, setSystemFeatures] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_annual: 0,
    features: [],
    feature_limits: {}, // { feature_name: limit_value }
    feature_display_texts: {}, // { feature_name: "3 Business Plans" or "Unlimited Users" }
    trial_enabled: false,
    trial_days: 0
  });

  // Load system features for proper display names
  useEffect(() => {
    const loadSystemFeatures = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/admin/system-features`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSystemFeatures(response.data.features || []);
        }
      } catch (error) {
        console.log('Could not load system features');
      }
    };
    loadSystemFeatures();
  }, []);

  // Get display name from system features or format it
  const getFeatureDisplayName = (featureName) => {
    const feature = systemFeatures.find(f => f.name === featureName);
    if (feature && feature.display_name) {
      // Check if it has proper spacing (contains spaces)
      if (feature.display_name.includes(' ')) {
        return feature.display_name;
      }
      // If display_name is malformed (all lowercase, no spaces), use the feature name instead
      // Example: "costcentermanagement" -> use "cost_center_management" instead
      if (feature.display_name.toLowerCase() === feature.display_name && !feature.display_name.includes('_')) {
        // It's all lowercase with no delimiters, use the snake_case name instead
        return formatFeatureName(feature.name);
      }
      // Otherwise format the display_name (handles PascalCase, camelCase)
      return formatFeatureName(feature.display_name);
    }
    // Fallback to formatting the feature name
    return formatFeatureName(featureName);
  };

  const openEditDialog = (tier) => {
    setEditingTier(tier);
    
    // Build feature_limits object from existing limits
    const featureLimits = {};
    if (tier.limits) {
      Object.keys(tier.limits).forEach(key => {
        featureLimits[key] = tier.limits[key];
      });
    }
    
    // Build feature_display_texts object (for pricing page display)
    const featureDisplayTexts = {};
    if (tier.feature_display_texts) {
      Object.keys(tier.feature_display_texts).forEach(key => {
        featureDisplayTexts[key] = tier.feature_display_texts[key];
      });
    } else {
      // Auto-generate default display texts if not set
      (tier.features || []).forEach(featureName => {
        const limit = featureLimits[featureName];
        const displayName = getFeatureDisplayName(featureName);
        if (limit === -1) {
          featureDisplayTexts[featureName] = `Unlimited ${displayName}`;
        } else if (typeof limit === 'number') {
          featureDisplayTexts[featureName] = `${limit} ${displayName}`;
        } else {
          featureDisplayTexts[featureName] = displayName;
        }
      });
    }
    
    setFormData({
      name: tier.name,
      display_name: tier.display_name || tier.name,
      description: tier.description || '',
      price_monthly: tier.price_monthly || 0,
      price_annual: tier.price_annual || 0,
      features: tier.features || [],
      feature_limits: featureLimits,
      feature_display_texts: featureDisplayTexts,
      trial_enabled: tier.trial_enabled || false,
      trial_days: tier.trial_days || 0
    });
    setShowEditDialog(true);
  };

  const handleUpdatePlan = async () => {
    try {
      // Reconstruct limits object from feature_limits
      const updatedData = {
        ...formData,
        limits: formData.feature_limits,
        feature_display_texts: formData.feature_display_texts
      };
      delete updatedData.feature_limits;
      
      // Remove 'name' from updatedData to ensure it doesn't change the tier name in Feature Governance
      delete updatedData.name;
      
      await axios.put(`${API}/admin/subscription-tiers/${editingTier.name}`, updatedData);
      toast.success("Plan updated successfully");
      setShowEditDialog(false);
      setEditingTier(null);
      onRefresh();
    } catch (error) {
      console.error('Update plan error:', error);
      toast.error(getErrorMessage(error, "Failed to update plan"));
    }
  };

  // Move feature up in the list
  const moveFeatureUp = (index) => {
    if (index === 0) return; // Already at top
    
    const newFeatures = [...formData.features];
    [newFeatures[index], newFeatures[index - 1]] = [newFeatures[index - 1], newFeatures[index]];
    
    setFormData({
      ...formData,
      features: newFeatures
    });
  };

  // Move feature down in the list
  const moveFeatureDown = (index) => {
    if (index === formData.features.length - 1) return; // Already at bottom
    
    const newFeatures = [...formData.features];
    [newFeatures[index], newFeatures[index + 1]] = [newFeatures[index + 1], newFeatures[index]];
    
    setFormData({
      ...formData,
      features: newFeatures
    });
  };

  if (!subscriptionTiers.length) {
    return (
      <div className="text-center py-8">
        <Crown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No subscription plans found</p>
        <p className="text-sm text-gray-500 mt-2">Plans are managed in Feature Governance</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Plans List */}
      <div className="space-y-3">
        {subscriptionTiers.filter(tier => tier.name !== 'admin').map((tier) => (
          <div key={tier.name} className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className="font-semibold text-lg">{tier.display_name || tier.name}</h4>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {tier.features?.length || 0} Features
                  </Badge>
                  {tier.trial_enabled && (
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      {tier.trial_days} Day Trial
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{tier.description}</p>
                <div className="flex items-center space-x-4 mt-2">
                  <span className="text-sm font-medium text-gray-900">
                    ${tier.price_monthly}/mo
                  </span>
                  <span className="text-sm text-gray-500">
                    ${tier.price_annual}/yr
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditDialog(tier)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Plan Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Plan - {editingTier?.display_name}</DialogTitle>
            <p className="text-sm text-gray-600">
              Configure plan details, pricing, and feature presentation
            </p>
          </DialogHeader>

          {editingTier && (
            <Tabs defaultValue="general" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">General Info</TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Trial</TabsTrigger>
                <TabsTrigger value="features">Features ({formData.features?.length || 0})</TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-y-auto mt-4 px-1">
                {/* General Info Tab */}
                <TabsContent value="general" className="space-y-6 mt-0">
                  {/* Plan Names */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">System Name (Read-only)</Label>
                      <Input
                        value={formData.name}
                        disabled
                        className="bg-gray-50 mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Managed in Feature Governance</p>
                    </div>

                    <div>
                      <Label htmlFor="display_name" className="text-sm font-medium">Display Name for Pricing Page</Label>
                      <Input
                        id="display_name"
                        value={formData.display_name}
                        onChange={(e) => setFormData({
                          ...formData,
                          display_name: e.target.value
                        })}
                        placeholder="e.g., Professional Plan"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">This name appears on the public pricing page</p>
                    </div>
                  </div>
                </TabsContent>

                {/* Pricing & Trial Tab */}
                <TabsContent value="pricing" className="space-y-6 mt-0">
                  {/* Pricing */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900">Pricing</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="price_monthly" className="text-sm">Monthly Price ($)</Label>
                        <Input
                          id="price_monthly"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.price_monthly}
                          onChange={(e) => setFormData({
                            ...formData,
                            price_monthly: parseFloat(e.target.value) || 0
                          })}
                          placeholder="49.99"
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="price_annual" className="text-sm">Annual Price ($)</Label>
                        <Input
                          id="price_annual"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.price_annual}
                          onChange={(e) => setFormData({
                            ...formData,
                            price_annual: parseFloat(e.target.value) || 0
                          })}
                          placeholder="490.00"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Trial Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900">Trial Period</h4>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="trial_enabled"
                        checked={formData.trial_enabled}
                        onChange={(e) => setFormData({
                          ...formData,
                          trial_enabled: e.target.checked
                        })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="trial_enabled" className="text-sm">Enable Free Trial</Label>
                    </div>
                    
                    {formData.trial_enabled && (
                      <div>
                        <Label htmlFor="trial_days" className="text-sm">Trial Duration (Days)</Label>
                        <Input
                          id="trial_days"
                          type="number"
                          min="1"
                          value={formData.trial_days}
                          onChange={(e) => setFormData({
                            ...formData,
                            trial_days: parseInt(e.target.value) || 0
                          })}
                          placeholder="14"
                          className="mt-1 w-32"
                        />
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Features Tab */}
                <TabsContent value="features" className="mt-0">
                  <p className="text-xs text-gray-600 mb-4">
                    Configure limits and display text for pricing page. Use arrows to reorder.
                  </p>
                  
                  {formData.features && formData.features.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      {formData.features.map((featureName, index) => {
                        const currentLimit = formData.feature_limits[featureName];
                        const displayText = formData.feature_display_texts[featureName] || '';
                        
                        // Determine the limit type: unlimited (-1) or custom number
                        let limitType = 'unlimited';
                        if (typeof currentLimit === 'number' && currentLimit !== -1) {
                          limitType = 'number';
                        }
                        
                        return (
                          <div key={featureName} className={`p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                            <div className="grid grid-cols-12 gap-4 items-start">
                              {/* Feature Name Column */}
                              <div className="col-span-3">
                                <p className="text-sm font-medium text-gray-900">{getFeatureDisplayName(featureName)}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{featureName}</p>
                              </div>
                              
                              {/* Limit Type Column */}
                              <div className="col-span-2">
                                <Label className="text-xs text-gray-600 mb-1 block">Limit Type</Label>
                                <Select
                                  value={limitType}
                                  onValueChange={(value) => {
                                    const newLimits = { ...formData.feature_limits };
                                    const newDisplayTexts = { ...formData.feature_display_texts };
                                    const displayName = getFeatureDisplayName(featureName);
                                    
                                    if (value === 'unlimited') {
                                      newLimits[featureName] = -1;
                                      newDisplayTexts[featureName] = `Unlimited ${displayName}`;
                                    } else if (value === 'number') {
                                      const numLimit = typeof currentLimit === 'number' && currentLimit > 0 && currentLimit !== -1 ? currentLimit : 1;
                                      newLimits[featureName] = numLimit;
                                      newDisplayTexts[featureName] = `${numLimit} ${displayName}`;
                                    }
                                    
                                    setFormData({
                                      ...formData,
                                      feature_limits: newLimits,
                                      feature_display_texts: newDisplayTexts
                                    });
                                  }}
                                >
                                  <SelectTrigger className="w-full text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unlimited">Unlimited</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Limit Value Column */}
                              {limitType === 'number' && (
                                <div className="col-span-1">
                                  <Label className="text-xs text-gray-600 mb-1 block">Value</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={typeof currentLimit === 'number' && currentLimit !== -1 ? currentLimit : ''}
                                    onChange={(e) => {
                                      const newLimits = { ...formData.feature_limits };
                                      const newDisplayTexts = { ...formData.feature_display_texts };
                                      const value = e.target.value === '' ? '' : parseInt(e.target.value);
                                      const displayName = getFeatureDisplayName(featureName);
                                      
                                      newLimits[featureName] = value;
                                      if (value !== '') {
                                        newDisplayTexts[featureName] = `${value} ${displayName}`;
                                      }
                                      
                                      setFormData({
                                        ...formData,
                                        feature_limits: newLimits,
                                        feature_display_texts: newDisplayTexts
                                      });
                                    }}
                                    className="text-sm h-9"
                                  />
                                </div>
                              )}
                              
                              {/* Display Text Column */}
                              <div className={limitType === 'number' ? 'col-span-5' : 'col-span-6'}>
                                <Label className="text-xs text-gray-600 mb-1 block">Pricing Page Display</Label>
                                <Input
                                  value={displayText}
                                  onChange={(e) => {
                                    const newDisplayTexts = { ...formData.feature_display_texts };
                                    newDisplayTexts[featureName] = e.target.value;
                                    setFormData({
                                      ...formData,
                                      feature_display_texts: newDisplayTexts
                                    });
                                  }}
                                  placeholder={`e.g., ${currentLimit === -1 ? 'Unlimited' : currentLimit} ${getFeatureDisplayName(featureName)}`}
                                  className="text-sm h-9"
                                />
                              </div>
                              
                              {/* Reorder Arrows Column */}
                              <div className="col-span-1 flex items-center justify-center gap-1 pt-5">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveFeatureUp(index)}
                                  disabled={index === 0}
                                  className="h-7 w-7 p-0"
                                  title="Move up"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveFeatureDown(index)}
                                  disabled={index === formData.features.length - 1}
                                  className="h-7 w-7 p-0"
                                  title="Move down"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 border rounded-lg">
                      <p className="text-sm">No features assigned to this tier</p>
                      <p className="text-xs mt-1">Assign features in Feature Governance tab</p>
                    </div>
                  )}
                </TabsContent>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingTier(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdatePlan}>
                  Save Changes
                </Button>
              </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// User Subscriptions Manager Component
const UserSubscriptionsManager = ({ subscriptions, users, subscriptionTiers, onRefresh }) => {
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTierId, setSelectedTierId] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleAssignSubscription = async () => {
    try {
      await axios.post(`${API}/user-subscription`, {
        user_id: selectedUserId,
        tier_id: selectedTierId,
        billing_cycle: billingCycle
      });
      toast.success("Subscription assigned successfully");
      setShowAssignDialog(false);
      setSelectedUserId('');
      setSelectedTierId('');
      setBillingCycle('monthly');
      onRefresh();
    } catch (error) {
      console.error('Assign subscription error:', error);
      toast.error(getErrorMessage(error, "Failed to assign subscription"));
    }
  };

  const handleUpdateSubscription = async (subscription, updates) => {
    try {
      await axios.put(`${API}/admin/subscriptions/${subscription.id}`, {
        ...subscription,
        ...updates
      });
      toast.success("Subscription updated successfully");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const handleCancelSubscription = async (subscription) => {
    if (!window.confirm("Are you sure you want to cancel this subscription?")) {
      return;
    }
    
    await handleUpdateSubscription(subscription, { status: 'cancelled' });
  };

  const getUser = (userId) => users.find(u => u.id === userId);
  const getTier = (tierId) => subscriptionTiers.find(t => t.id === tierId || t.name === tierId);

  if (!subscriptions.length) {
    return (
      <div className="text-center py-8">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No user subscriptions found</p>
        <Button 
          className="mt-4" 
          onClick={() => setShowAssignDialog(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Assign First Subscription
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Subscription Button */}
      <Button 
        onClick={() => setShowAssignDialog(true)}
        size="sm"
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Assign New Subscription
      </Button>

      {/* Subscriptions List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {subscriptions.map((subscription) => {
          const user = getUser(subscription.user_id);
          const tier = getTier(subscription.tier_id);
          
          return (
            <div key={subscription.id} className="border rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <h5 className="font-medium">{user?.full_name || 'Unknown User'}</h5>
                    <Badge 
                      variant={subscription.status === 'active' ? "default" : "secondary"}
                      className={
                        subscription.status === 'active' ? "bg-green-100 text-green-700" : 
                        subscription.status === 'cancelled' ? "bg-red-100 text-red-700" :
                        "bg-yellow-100 text-yellow-700"
                      }
                    >
                      {subscription.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm font-medium">{tier?.display_name || tier?.name || 'Unknown Plan'}</span>
                    <span className="text-xs text-gray-500">
                      {subscription.billing_cycle} billing
                    </span>
                    <span className="text-xs text-gray-500">
                      ${subscription.billing_cycle === 'monthly' ? tier?.price_monthly : tier?.price_annual}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <Select 
                    value={subscription.tier_id} 
                    onValueChange={(newTierId) => handleUpdateSubscription(subscription, { tier_id: newTierId })}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {subscriptionTiers.filter(t => t.is_active).map(tier => (
                        <SelectItem key={tier.id} value={tier.id}>
                          {tier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {subscription.status === 'active' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelSubscription(subscription)}
                      className="text-red-600 h-8"
                    >
                      <XCircle className="h-3 w-3" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateSubscription(subscription, { status: 'active' })}
                      className="text-green-600 h-8"
                    >
                      <CheckCircle className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Assign Subscription Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Subscription</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Select Plan</Label>
              <Select value={selectedTierId} onValueChange={setSelectedTierId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionTiers.filter(t => t.is_active).map(tier => (
                    <SelectItem key={tier.id} value={tier.id}>
                      {tier.name} - ${tier.price_monthly}/month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Billing Cycle</Label>
              <Select value={billingCycle} onValueChange={setBillingCycle}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAssignDialog(false);
                  setSelectedUserId('');
                  setSelectedTierId('');
                  setBillingCycle('monthly');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignSubscription}
                disabled={!selectedUserId || !selectedTierId}
              >
                Assign Subscription
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;